import { type AlertProps } from '@mui/material'

import type React from 'react'

export const toastContainerElement: HTMLDivElement =
  document.createElement('div')
const popoverContainer = document.getElementById('popover-portal-container')

// This if/else is for unit tests that don't use the index.html file
if (popoverContainer) {
  popoverContainer.appendChild(toastContainerElement)
} else {
  document.body.appendChild(toastContainerElement)
}

let idCounter = 100 // arbitrarily high number to get 2 character ids

/**
 * @property action - MUI Alert action <https://mui.com/material-ui/react-alert/#actions>
 * @property closeOnClick - Allow toast to be closed on click.
 *   If `false` and no `action` provided, the close icon (x) is rendered.
 * @property testKey - defaults to ''. Used as the value of the `data-test-toast`
 * attribute for e2e test hooks
 * @property text - Content of the toast as string, react node, or function.
 *   If a function, it'll be called with argument `{ close, toast }` -- where `close`
 *   can be used to close the toast
 * @property time - length in milliseconds for alert to be on screen.
 * @property type - defaults to 'info'
 */
export interface ToastOptions {
  action?: AlertProps['action']
  closeOnClick?: boolean
  testKey?: string
  text?: string | React.ReactNode | ToastDynamicContent
  time?: number
  type?: AlertProps['severity']
}

export type ToastDynamicContent = ({
  close,
  toast
}: {
  close: () => void
  toast: ToastOptions
}) => React.ReactNode

export default function toast({
  action,
  closeOnClick = true,
  testKey = '',
  text = '',
  time,
  type = 'info'
}: ToastOptions): void {
  const id = (++idCounter).toString(36) // generate base36 ids
  const toastTime = time == null ? (action ? 10000 : 5000) : time

  const toast = {
    type,
    text,
    action,
    time: toastTime,
    closeOnClick,
    testKey,
    createdAt: Date.now(),
    endAt: Date.now() + toastTime,
    id,
    enabled: true
  }

  toastContainerElement.dispatchEvent(
    new CustomEvent('toast:add', {
      detail: toast
    })
  )
}

export function errorToast(
  errorMessage?: string,
  options?: Omit<ToastOptions, 'type' | 'text'>
) {
  toast({
    ...options,
    type: 'error',
    text:
      errorMessage ?? 'An unexpected error occurred. Please try again later.'
  })
}

export function successToast(
  successMessage: string,
  options?: Omit<ToastOptions, 'type' | 'text'>
) {
  toast({
    ...options,
    type: 'success',
    text: successMessage
  })
}
